import { useState } from "react"
import Break from "componentsUnits/break"
import Button from "componentsUnits/button"
import ImageComponent from "componentsUnits/image"
import ImageInputComponent from "componentsUnits/imageInput"
import Row from "componentsUnits/row"
import { Size } from "types/size"
import { Type } from "types/type"
import { useAppDispatch, useAppSelector } from 'reducers/storeHooks'
import { Post } from "types/post"
import { EntryType, getHumanEntryType } from "actions/general"
import VideoInputComponent from "componentsUnits/videoInput"
import VideoComponent from "componentsUnits/video"
import TextAreaComponent from "componentsUnits/textArea"
import { uploadImagePost, uploadVideoPost, createPost } from "actions/post"
import FormComponent from "components/Form"
import { isDev } from "app/general"
import { isProfane } from "helpers/badWords"
import RowComponent from "componentsUnits/row"
import SelectInputComponent from "componentsUnits/selectInput"
import Modal from "componentsUnits/modal"
import ColumnComponent from "componentsUnits/columnComponent"
import Subtitle from "componentsUnits/subtitle"
import ButtonText from "componentsUnits/buttonText"
import IconComponent from "componentsUnits/icon"
import styles from "./PostForm.module.scss"

interface Props {
    refresh?: () => unknown
    parentId?: string
    parentType?: EntryType
    type?: Type
}
export const PostForm: React.FC<Props> = (props: Props) => {
    const dispatch = useAppDispatch()
    const [showModal, setShowModal] = useState(false)
    const uid = useAppSelector((state) => state.userReducer.user?.uid)
    const [entryType, setEntryType] = useState<EntryType>(EntryType.POST)
    const creatorProfileImage = useAppSelector((state) => state.profileReducer.profile?.profileImage)
    const creatorName = useAppSelector((state) => state.profileReducer.profile?.name)
    const [postText, setPostText] = useState('')
    const [imageToUpload, setImageToUpload] = useState('')
    const [videoToUpload, setVideoToUpload] = useState('')
    const [imageToShow, setImageToShow] = useState('')
    const [videoToShow, setVideoToShow] = useState('')
    const submitPost = async () => {
        if (postText.length === 0) {
            return alert('Please write something before posting')
        };

        if (isProfane(postText)) {
            return alert('Please refrain from using profanity')
        }

        if (!uid) {
            isDev && console.log('No uid')
            return
        }
        if (!creatorName) {
            isDev && console.log('No creator name')
            return
        }
        const post: Post = {
            description: postText,
            uid: uid,
            createdAt: new Date().getTime().toString(),
            likesIds: {},
            creatorProfileImage: creatorProfileImage,
            creatorName: creatorName,
            profileImage: '',
            id: '',
            title: '',
            entryType: entryType,
            parentId: props.parentId,
            parentType: props.parentType,
        }
        try {
            let success;
            if (imageToUpload) {
                success = await dispatch(uploadImagePost(imageToUpload, post))
            }
            else if (videoToUpload) {
                success = await dispatch(uploadVideoPost(videoToUpload, post))
            }
            else {
                success = await dispatch(createPost(post))
            }
            isDev && console.log(success)

            if (success) {
                setPostText('')
                setImageToUpload('')
                setVideoToUpload('')
                setTimeout(() => {
                    if (props.refresh) props.refresh()
                }, 3000)
            }

        } catch (error) {
            isDev && console.log(error)
        }
    }

    const resetUpload = () => {
        setImageToUpload('')
        setVideoToUpload('')
        setImageToShow('')
        setVideoToShow('')
    }

    const handlePostTypeClick = (e: EntryType) => {
        setEntryType(EntryType[e])
        setShowModal(false)
    }
    return (
        <div>
            <FormComponent type={props.type ?? Type.t1} smallMargins fullWidth>
                {imageToUpload
                    && <> <ImageComponent
                        onCloseButton={resetUpload}
                        image={imageToShow}
                    />
                        <Break /> </>}

                {videoToUpload
                    && <> <VideoComponent
                        onCloseButton={resetUpload}
                        video={videoToShow}
                    />
                        <Break /> </>}

                <TextAreaComponent
                    placeholder="Write a post..."
                    // icon="plus"
                    smallPadding
                    type="text"
                    text={postText}
                    onChange={setPostText}
                // label="Post"
                />
                <RowComponent noPadding gap fullWidth spaceBetween>
                    <ColumnComponent centerV>
                        <Break size={Size.small} />
                        <ButtonText
                            type={Type.light}
                            size={Size.small}
                            text={getHumanEntryType(entryType)}
                            onClick={() => setShowModal(true)}
                        />
                        {/* <SelectInputComponent
                            type={""}
                            isSelect
                            options={generateOptions()}
                            onChange={(option: string) => setEntryType(EntryType[option as keyof typeof EntryType])}
                        /> */}
                    </ColumnComponent>
                    <div >
                        <Break size={Size.small} />
                        <Row gap noPadding>
                            <ImageInputComponent
                                size="xSmall"
                                showImage={false}
                                setImageToUpload={(img: any) => {
                                    setImageToUpload(img)
                                    setVideoToUpload("")
                                }}
                                setImageToShow={(img: any) => {
                                    setImageToShow(img)
                                    setVideoToShow("")
                                }}
                                justIcon
                            />

                            <VideoInputComponent
                                video={videoToUpload}
                                size="xSmall"
                                showVideo={false}
                                setVideoToUpload={(video: any) => {
                                    setVideoToUpload(video)
                                    setImageToUpload("")
                                }}
                                setVideoToShow={(video: any) => {
                                    setVideoToShow(video)
                                    setImageToShow("")
                                }}
                                justIcon
                            />

                            <Button
                                text="Post"
                                type="white"
                                size={Size.xxSmall}
                                onClick={submitPost}
                            />
                        </Row>
                    </div>
                </RowComponent>
            </FormComponent>

            <Modal
                delayInSeconds={0}
                onClose={() => setShowModal(false)}
                header="Post Type"
                hidden={!showModal}
            >
                <ColumnComponent centerH centerV>
                    <Break size={Size.small} />
                    <RowComponent gap>
                        <ColumnComponent width="100px" centerH onClick={() => handlePostTypeClick(EntryType.POST)} >
                            <img src="images/post.png" className={styles.image} />
                            <Break size={Size.xSmall} />
                            <Subtitle text={getHumanEntryType(EntryType.POST)} type={Type.dark} size={Size.xSmall} />
                            <Break size={Size.xSmall} />
                        </ColumnComponent>

                        <ColumnComponent width="100px" centerH onClick={() => handlePostTypeClick(EntryType.TICKET)} >
                            <img src="images/sell_ticket.png" className={styles.image} />
                            <Break size={Size.xSmall} />
                            <Subtitle text={getHumanEntryType(EntryType.TICKET)} type={Type.dark} size={Size.xSmall} />
                            <Break size={Size.xSmall} />
                        </ColumnComponent>

                        <ColumnComponent width="100px" centerH onClick={() => handlePostTypeClick(EntryType.PRODUCT)} >
                            <img src="images/sell_item.png" className={styles.image} />
                            <Break size={Size.xSmall} />
                            <Subtitle text={getHumanEntryType(EntryType.PRODUCT)} type={Type.dark} size={Size.xSmall} />
                            <Break size={Size.xSmall} />
                        </ColumnComponent>
                    </RowComponent>
                    {/* 
                    <RowComponent gap>
                        <ColumnComponent shadow centerH width={"100px"} onClick={() => handlePostTypeClick(EntryType.SERVICE)} >
                            <img src="images/dancing-teaching-session-adults.png" className={styles.image} />
                            <Break size={Size.xSmall} />
                            <Subtitle text={getHumanEntryType(EntryType.SERVICE)} type={Type.dark} size={Size.xSmall} />
                            <Break size={Size.xSmall} />
                        </ColumnComponent>

                        <ColumnComponent shadow width="100px" centerH onClick={() => handlePostTypeClick(EntryType.JOB)} >
                            <img src="images/normal-dancing-post.png" className={styles.image} />
                            <Break size={Size.xSmall} />
                            <Subtitle text={getHumanEntryType(EntryType.JOB)} type={Type.dark} size={Size.xSmall} />
                            <Break size={Size.xSmall} />
                        </ColumnComponent>
                    </RowComponent> */}
                    <Break />
                </ColumnComponent>
            </Modal>
        </div>
    )
}